import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, X } from 'lucide-react';
import { api } from '../api/client';
import { useSearchStore } from '../stores/searchStore';
import { DH_COMPONENT_STATUS_SKIN, dhSkin } from '../lib/downhole-labels';
import { useAuth } from '../contexts/AuthContext';
import { fieldSearchGroups, recordAudience, recordPath, type RecordAudience } from '../lib/record-paths';
import { LoadFailureNotice } from './ui/LoadFailureNotice';

type RawResult = Record<string, any>;

type SearchResponse = {
  bills_of_lading?: RawResult[];
  jobs?: RawResult[];
  equipment?: RawResult[];
  drivers?: RawResult[];
  vehicles?: RawResult[];
  customers?: RawResult[];
  rental_assets?: RawResult[];
  downhole_assets?: RawResult[];
};

type GroupKey = keyof SearchResponse;

const GROUP_ORDER: GroupKey[] = [
  'bills_of_lading',
  'jobs',
  'equipment',
  'drivers',
  'vehicles',
  'customers',
  'rental_assets',
  // Restored with the /downhole/* routes (fieldiq-api#882). The API only
  // returns this group for a tenant with downhole_tools enabled, so a casing
  // or trucking tenant's results do not change.
  'downhole_assets',
];

const GROUP_LABEL: Record<GroupKey, string> = {
  bills_of_lading: 'Bills of Lading',
  jobs: 'Jobs',
  equipment: 'Equipment',
  drivers: 'Drivers',
  vehicles: 'Vehicles',
  customers: 'Customers',
  rental_assets: 'Rental Equipment',
  downhole_assets: 'Downhole Assets',
};

type FlatResult = {
  group: GroupKey;
  item: RawResult;
  path: string;
  primary: string;
  secondary: string;
};

function dash(v: any): string {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

function pickPrimary(group: GroupKey, item: RawResult): string {
  switch (group) {
    case 'bills_of_lading':
      return dash(item.bol_number ?? item.bolNumber ?? item.number);
    case 'jobs':
      return dash(item.job_number ?? item.jobNumber ?? item.number ?? item.title);
    case 'equipment':
      // Casing equipment (api#1138): name, unit number, serial, status.
      return dash(item.name ?? item.unit_number ?? item.serial_number);
    case 'drivers': {
      // The API sends first_name / last_name (search.routes.ts). It has never
      // sent `name`, `driver_name` or `full_name`, so the old chain resolved to
      // undefined and every driver rendered as an em dash.
      const full = [item.first_name, item.last_name].filter(Boolean).join(' ');
      return dash(full || null);
    }
    case 'vehicles':
      return dash(item.unit_number ?? item.unitNumber ?? item.license_plate ?? item.name);
    case 'customers':
      return dash(item.name ?? item.customer_name);
    case 'rental_assets':
      // `asset_number` is the field the API actually sends; it was absent from
      // this chain, so the row silently fell through to the serial number -- a
      // plausible-looking value that is not the identifier the tenant uses.
      return dash(item.asset_number ?? item.asset_tag ?? item.tag ?? item.serial_number ?? item.name);
    case 'downhole_assets':
      return dash(item.asset_number ?? item.serial_number ?? item.name ?? item.description);
  }
}

function pickSecondary(group: GroupKey, item: RawResult): string {
  switch (group) {
    case 'bills_of_lading': {
      const parts = [item.customer_name, item.status].filter(Boolean);
      return parts.length ? parts.join(' · ') : '—';
    }
    case 'jobs': {
      const parts = [item.customer_name, item.vertical, item.status].filter(Boolean);
      return parts.length ? parts.join(' · ') : '—';
    }
    case 'equipment': {
      const parts = [item.unit_number, item.serial_number, item.status].filter(Boolean);
      return parts.length ? parts.join(' · ') : '—';
    }
    case 'drivers': {
      const driverNum = item.driver_number ?? null;
      const parts = [driverNum ? `#${driverNum}` : null, item.status].filter(Boolean);
      return parts.length ? parts.join(' · ') : '—';
    }
    case 'vehicles': {
      const parts = [
        [item.year, item.make, item.model].filter(Boolean).join(' '),
        item.status,
      ].filter(Boolean);
      return parts.length ? parts.join(' · ') : '—';
    }
    case 'customers': {
      const parts = [item.email, item.phone].filter(Boolean);
      return parts.length ? parts.join(' · ') : '—';
    }
    case 'rental_assets': {
      const parts = [item.description ?? item.category, item.status].filter(Boolean);
      return parts.length ? parts.join(' · ') : '—';
    }
    case 'downhole_assets': {
      // search.routes.ts sends id, serial_number and status only. The status is
      // a dh_component_status value, so it goes through the downhole label map
      // rather than printing `in_hole`; an unrecognised one is left out.
      const status = item.status ? dhSkin(DH_COMPONENT_STATUS_SKIN, item.status).label : null;
      const parts = [item.description, status && status !== '—' ? status : null].filter(Boolean);
      return parts.length ? parts.join(' · ') : '—';
    }
  }
}

// Returns navigation path for a result. For entities without a detail route,
// fall back to the closest collection page (route audit: see PR).
function buildPath(group: GroupKey, item: RawResult, audience: RecordAudience): string | null {
  const id = item.id ?? item.uuid;
  // Jobs, equipment and BOLs open where this audience has a page for them
  // (lib/record-paths.ts). Everything else is an office record: the office keeps
  // its collection pages, and a field audience gets null and the row is left out.
  if (group === 'jobs' || group === 'equipment' || group === 'bills_of_lading') {
    const kind = group === 'jobs' ? 'job' : group === 'equipment' ? 'equipment' : 'bol';
    if (id) return recordPath(kind, String(id), audience);
    if (audience !== 'office') return null;
    return group === 'jobs' ? '/jobs' : group === 'equipment' ? '/equipment' : '/trucking/bols';
  }
  if (audience !== 'office') return null;
  switch (group) {
    case 'drivers':
      return '/trucking/drivers';
    case 'vehicles':
      return '/trucking/fleet';
    case 'customers':
      return id ? `/customers/${id}` : '/customers';
    case 'rental_assets':
      return id ? `/rentals/assets/${id}` : '/rentals/assets';
    case 'downhole_assets':
      return id ? `/downhole/components/${id}` : '/downhole/components';
  }
}

export default function GlobalSearchModal() {
  const isOpen = useSearchStore((s) => s.isOpen);
  const close = useSearchStore((s) => s.close);
  const navigate = useNavigate();
  const { isFieldWorker, hasVertical } = useAuth();
  const audience = recordAudience(!!isFieldWorker, hasVertical);
  // What a field audience can find, for the placeholder only. Which rows show is
  // decided once, by buildPath: a record this tree has no page for gets null and
  // is left out. (A second filter here was the same rule twice, and a mutation
  // deleting it reddened nothing.)
  const fieldGroups = audience === 'office' ? [] : fieldSearchGroups(audience, hasVertical);

  const [query, setQuery] = useState('');
  // A failed search is not "No matches" (CLAUDE.md: a refused or unreachable
  // read is its own state). Held apart from `results` so the two cannot be
  // confused.
  const [failure, setFailure] = useState<unknown>(null);
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults(null);
      setFailure(null);
      setLoading(false);
      setHighlighted(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      abortRef.current?.abort();
      abortRef.current = null;
    }
  }, [isOpen]);

  // Debounced search effect
  useEffect(() => {
    if (!isOpen) return;
    const q = query.trim();
    if (q.length < 2) {
      abortRef.current?.abort();
      abortRef.current = null;
      setResults(null);
      setLoading(false);
      return;
    }

    const handle = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      setFailure(null);

      api.search
        .query(q, 5, controller.signal)
        .then((res) => {
          if (controller.signal.aborted) return;
          // GET /search returns { results, query, total_count } -- NOT the
          // { success, data } envelope the rest of the API uses. Read `.results`
          // by name. Do NOT reach for a `??` chain here: the previous
          // `res?.data?.data ?? res?.data` fell through to the WRAPPER object,
          // whose group keys are all undefined, so every search rendered
          // "No matches" from the day it shipped. A longer chain preserves that
          // failure mode -- a wrong shape must be undefined, not a lookalike.
          setResults((res?.data?.results ?? {}) as SearchResponse);
          setLoading(false);
          setHighlighted(0);
        })
        .catch((err: any) => {
          if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
          if (controller.signal.aborted) return;
          setResults(null);
          setFailure(err);
          setLoading(false);
        });
    }, 200);

    return () => clearTimeout(handle);
  }, [query, isOpen]);

  const flat: FlatResult[] = useMemo(() => {
    if (!results) return [];
    const out: FlatResult[] = [];
    for (const group of GROUP_ORDER) {
      const items = results[group];
      if (!items || items.length === 0) continue;
      for (const item of items) {
        const path = buildPath(group, item, audience);
        if (!path) continue;
        out.push({
          group,
          item,
          path,
          primary: pickPrimary(group, item),
          secondary: pickSecondary(group, item),
        });
      }
    }
    return out;
  }, [results, audience]);

  const navigateToResult = useCallback(
    (r: FlatResult) => {
      navigate(r.path);
      close();
    },
    [navigate, close],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (flat.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlighted((i) => Math.min(flat.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlighted((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const target = flat[highlighted];
        if (target) navigateToResult(target);
      }
    },
    [flat, highlighted, navigateToResult, close],
  );

  if (!isOpen) return null;

  const trimmed = query.trim();
  const showEmptyHint = trimmed.length === 0;
  const showKeepTyping = trimmed.length > 0 && trimmed.length < 2;
  const showNoResults =
    !loading && !failure && trimmed.length >= 2 && results !== null && flat.length === 0;

  // Grouped render data, built from `flat` so the rows drawn and the rows the
  // arrow keys walk are the same list (a field audience drops some).
  const grouped: { group: GroupKey; rows: { idx: number; item: RawResult; primary: string; secondary: string; path: string }[] }[] = [];
  flat.forEach((r, idx) => {
    const last = grouped[grouped.length - 1];
    const row = { idx, item: r.item, primary: r.primary, secondary: r.secondary, path: r.path };
    if (last && last.group === r.group) last.rows.push(row);
    else grouped.push({ group: r.group, rows: [row] });
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
      onKeyDown={onKeyDown}
    >
      <div
        className="fixed inset-0 bg-black/60"
        onClick={close}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-label="Global search"
        className="relative z-50 w-full max-w-2xl bg-fieldiq-card border border-fieldiq-border rounded-2xl shadow-xl overflow-hidden"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-fieldiq-border">
          <Search size={18} className="text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              audience === 'office'
                ? 'Search BOLs, jobs, drivers, customers...'
                : `Search ${fieldGroups.map((g) => GROUP_LABEL[g].toLowerCase()).join(' and ')}...`
            }
            className="flex-1 bg-transparent text-foreground placeholder-muted-foreground text-base focus:outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <Loader2 size={16} className="animate-spin text-muted-foreground" />}
          <button
            onClick={close}
            className="p-1 text-muted-foreground hover:text-foreground rounded"
            aria-label="Close search"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {showEmptyHint && (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              Start typing to search across your data...
            </div>
          )}
          {showKeepTyping && (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              Keep typing...
            </div>
          )}
          {failure != null && trimmed.length >= 2 && (
            <div className="px-4 py-6">
              <LoadFailureNotice error={failure} subject="search results" />
            </div>
          )}
          {showNoResults && (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              No matches for "{trimmed}"
            </div>
          )}

          {grouped.map(({ group, rows }) => (
            <div key={group} className="py-1">
              <div className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {GROUP_LABEL[group]}
              </div>
              {/* Labelled so each group is one addressable list for a screen
                  reader -- and, incidentally, for a test that needs to read the
                  rows of exactly one group. */}
              <ul aria-label={GROUP_LABEL[group]}>
                {rows.map((r) => {
                  const active = r.idx === highlighted;
                  return (
                    <li key={`${group}-${r.item.id ?? r.idx}`}>
                      <button
                        type="button"
                        onMouseEnter={() => setHighlighted(r.idx)}
                        onClick={() =>
                          navigateToResult({
                            group,
                            item: r.item,
                            primary: r.primary,
                            secondary: r.secondary,
                            path: r.path,
                          })
                        }
                        className={`w-full text-left px-4 py-2.5 flex flex-col gap-0.5 transition-colors ${
                          active ? 'bg-secondary/60' : 'hover:bg-secondary/30'
                        }`}
                      >
                        <span className="text-sm font-medium text-foreground truncate">
                          {r.primary}
                        </span>
                        <span className="text-xs text-muted-foreground truncate">
                          {r.secondary}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="hidden sm:flex items-center justify-between gap-4 px-4 py-2 border-t border-fieldiq-border text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <span><kbd className="px-1.5 py-0.5 rounded border border-fieldiq-border bg-background">↑</kbd> <kbd className="px-1.5 py-0.5 rounded border border-fieldiq-border bg-background">↓</kbd> Navigate</span>
            <span><kbd className="px-1.5 py-0.5 rounded border border-fieldiq-border bg-background">↵</kbd> Open</span>
            <span><kbd className="px-1.5 py-0.5 rounded border border-fieldiq-border bg-background">Esc</kbd> Close</span>
          </div>
        </div>
      </div>
    </div>
  );
}
